'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { css } from '@bts-web/utils-style-engine';
import {
  intlAssetValueFormatting,
  getRoundedPrecision,
  assetUnitsValueToPercentage,
  intlFiatValueFormatting,
} from '@bts-web/utils-formatting';
import {
  defaultLanguage,
  getLanguageFromLocale,
  getPluralTranslationKey,
  interpolateTranslation,
  useClientTranslation,
} from '@bts-web/utils-lokalise';
import { AssetTypeName } from '../../../portfolio/utils/types';
import { PriceVariation } from '../PriceVariation/PriceVariation.use-client';
import { AssetGroupEdgesType, AssetGroupsType } from '../../types';
import { AccordionProps } from '@bts-web/design-system/component/accordion';
import { Accordion } from '../Accordion/Accordion';
import { defaultAssetImg, IslamicStar } from '../../assets';
import { Status } from '../Status/Status';
import { isEquityAsset } from '../../utils/assets/isEquityAsset';
import { AssetType } from '../../../asset-details/types';
import { assetImageByName, PortfolioAssetOnClickActions } from './utils';
import { getAppConfig } from '@bts-web/core-app-config';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { getAssetTradeUrl } from '../../../route-builders';
import {
  getPortfolioTranslations,
  PortfolioContentTranslationsKeys,
} from '../../../portfolio/utils/getPortfolioTranslations';
import { TradePriceType } from '../../../orders/types';
import { useClientUserInfo } from '../../userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { useFeatureFlag } from '../../../statsig/sdk/provider';

export type PortfolioAssetsTranslations = {
  crypto: string;
  etfs: string;
  metals: string;
  stocks: string;
  etcs: string;
};

interface getAssetGroupContentsProps {
  locale: string;
  actionMode: PortfolioAssetOnClickActions;
  totalAssets?: number | null;
  edges: AssetGroupEdgesType;
  router: ReturnType<typeof useRouter>;
  translations: PortfolioContentTranslationsKeys;
  priceType: TradePriceType;
  isIslamicUser?: boolean;
  isLegacyEnabled: boolean;
  legacyLabel: string;
}

export interface PortfolioAssetsProps {
  actionMode: PortfolioAssetOnClickActions;
  assetGroups: NonNullable<AssetGroupsType>;
  locale: string;
  loading?: boolean;
  onExpand?: () => void;
}

const getHrefBasedOnActionMode = (
  assetId: string,
  actionMode: PortfolioAssetOnClickActions,
  priceType: TradePriceType,
) => {
  if (actionMode === PortfolioAssetOnClickActions.GO_TO_ASSET_DETAILS) {
    return `${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId)}?ref=portfolio`;
  }

  if (actionMode === PortfolioAssetOnClickActions.GO_TO_MY_ASSETS) {
    const isOrder = priceType === TradePriceType.STOP;

    return isOrder
      ? getAssetTradeUrl(assetId, { type: 'orderForm', orderType: 'SELL' })
      : getAssetTradeUrl(assetId, { type: 'sell' });
  }
};

const getAssetGroupContents = ({
  edges,
  totalAssets,
  locale,
  translations,
  actionMode,
  priceType,
  isIslamicUser,
  isLegacyEnabled,
  legacyLabel,
}: getAssetGroupContentsProps) => {
  const { currency } = getAppConfig();

  return edges?.map((edge) => {
    const edgeTotalReturnPercentage = Number(edge?.totalReturn?.percentage);

    if (!edge?.node?.id) {
      return {
        subtitle: translations.assetUnavailableTitle,
      };
    }

    return {
      title: edge?.node?.name,
      subtitle: interpolateTranslation(translations.unitAmount, {
        amount: intlAssetValueFormatting(
          getRoundedPrecision(edge?.assetBalance),
          {
            locale,
            fractionDigits: edge?.assetBalance?.precision,
          },
        ),
      }),
      titleBadge: (() => {
        const isLegacy =
          isLegacyEnabled &&
          !isEquityAsset(edge?.node?.__typename ?? '') &&
          edge?.node?.__typename !== AssetType.METAL;

        if (!isIslamicUser && !isLegacy) return null;

        return (
          <>
            {isLegacy && (
              <Status
                text={legacyLabel}
                size="small"
                visual="filledNeutralLight"
              />
            )}
            {isIslamicUser && (
              <IslamicStar
                className={css({
                  width: '16px',
                  height: '16px',
                  color: 'brand.fill_primary',
                })}
              />
            )}
          </>
        );
      })(),
      totalAssets: totalAssets,
      image: edge?.node?.logoUrl,
      fallbackImage: defaultAssetImg,
      href: getHrefBasedOnActionMode(
        edge?.node?.id as string,
        actionMode,
        priceType,
      ),
      id: edge?.node?.id as string,
      ContentIndicatorSlot: (
        <PriceVariation
          value={intlFiatValueFormatting(
            getRoundedPrecision(edge?.fiatBalance),
            {
              locale,
              currency,
              fractionDigits: edge?.fiatBalance?.precision,
            },
          )}
          percentage={assetUnitsValueToPercentage(edgeTotalReturnPercentage, {
            isAbsolute: true,
            locale,
          })}
          visual={
            edgeTotalReturnPercentage > 0 ? 'filledPositive' : 'filledNegative'
          }
          size="smallAccent"
          trend={edgeTotalReturnPercentage > 0 ? 'positive' : 'negative'}
          valuePosition="bottom"
        />
      ),
    };
  });
};

const PortfolioAssets = ({
  actionMode,
  assetGroups,
  locale,
  loading,
  onExpand,
}: PortfolioAssetsProps) => {
  const router = useRouter();

  const searchParams = useSearchParams();

  const priceType = searchParams.get('priceType') as TradePriceType;

  const { currency } = getAppConfig();

  const { t } = useClientTranslation();

  const language = getLanguageFromLocale(locale) ?? defaultLanguage;

  const translations = getPortfolioTranslations(t);

  const { isIslamicUser } = useClientUserInfo();

  const feature_hasRealStocksEnabled = useFeatureFlag(
    'feature_hasRealStocksEnabled',
  );

  const assetTitleByName: Record<AssetTypeName, string> = {
    [AssetTypeName.COIN]: translations.crypto,
    [AssetTypeName.METAL]: translations.metals,
    [AssetTypeName.STOCK]: translations.stocks,
    [AssetTypeName.ETF]: translations.etfs,
    [AssetTypeName.ETC]: translations.etcs,
  };

  const accordionItems: AccordionProps['external']['items'] = [...assetGroups]
    .sort((itemA, itemB) => Number(itemA.order) - Number(itemB.order))
    .map((assetGroup) => {
      const totalReturnPercentage = Number(assetGroup.totalReturn.percentage);

      const firstAssetGroup = assetGroup?.node?.[0];

      const ItemImage = assetImageByName[assetGroup.name as AssetTypeName];

      const totalAssetsCount = assetGroup?.totalAssets ?? 0;

      const assetGroupAmountTemplate = t(
        getPluralTranslationKey(
          'dashboard_portfolio_asset_amount',
          totalAssetsCount,
          language,
        ),
      );

      return {
        value: assetGroup.name,
        header: {
          title: assetTitleByName[assetGroup.name as AssetTypeName],
          subtitle: interpolateTranslation(assetGroupAmountTemplate, {
            0: totalAssetsCount,
          }),
          totalAssets: totalAssetsCount,
          image: ItemImage ? <ItemImage /> : null,
          ContentIndicatorSlot: (
            <PriceVariation
              value={intlFiatValueFormatting(
                getRoundedPrecision(assetGroup.fiatBalance),
                {
                  locale,
                  currency,
                  fractionDigits: assetGroup.fiatBalance?.precision,
                },
              )}
              percentage={assetUnitsValueToPercentage(totalReturnPercentage, {
                isAbsolute: true,
                locale,
              })}
              visual={
                totalReturnPercentage > 0 ? 'filledPositive' : 'filledNegative'
              }
              size="smallAccent"
              trend={totalReturnPercentage > 0 ? 'positive' : 'negative'}
              valuePosition="bottom"
            />
          ),
        },
        contents: getAssetGroupContents({
          edges: firstAssetGroup?.edges || [],
          totalAssets: assetGroup?.totalAssets,
          locale,
          translations,
          router,
          actionMode,
          priceType,
          isIslamicUser,
          isLegacyEnabled: feature_hasRealStocksEnabled,
          legacyLabel: t('legacy'),
        }),
      };
    }) as AccordionProps['external']['items'];

  return (
    <Accordion
      items={accordionItems}
      lastItemId="latest-transactions"
      loading={loading}
      handleOnExpand={onExpand}
    />
  );
  // ! DO NOT REMOVE lastItemId prop - this is attached here to serve as an achor link (#latest-transactions) for the Latest Transactions section on Portfolio
  // ? the anchor cannot be attached directly on the Latest Transactions section's heading because the mobile floating header will cover it;
  // ? due to this the scroll needs to be set on an element positioned above it when opening the portfolio link with the anchor attached (/portfolio#latest-transactions)
};

export { PortfolioAssets };
